import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ModalProps,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import type { BankAccount } from '../../../../../types';

export type StateReasonModalType = 'show' | 'edit';
type StateReasonModalProps = {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  modalType: StateReasonModalType;
  activeReason: string;
  fields: Partial<BankAccount> | null;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
};

export const StateReasonModal: FC<StateReasonModalProps> = (props) => {
  const { open, onClose, onCancel, modalType, activeReason, fields, fieldsHandler } = props;
  const { t } = useTranslation();

  // variables
  const isShowType = modalType === 'show';
  const isEditType = modalType === 'edit';

  // validation for required fields
  const requiredFields = ['state_reason'] as (keyof Partial<BankAccount>)[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '' || fields[field] === null) {
        return true;
      }
    }

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    isEditType && onCancel();
    onClose();
  };
  const closeHandler = () => {
    isEditType && onCancel();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>
        {isEditType
          ? t('profile-bank-accounts.state-reason-modal.title-edit')
          : t('profile-bank-accounts.state-reason-modal.title-show')}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="state_reason"
            value={isShowType ? (activeReason ?? '') : (fields?.state_reason ?? '')}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-bank-accounts.state-reason-modal.reason')}
            fullWidth
            multiline
            minRows={3}
            inputProps={{ readOnly: isShowType }}
          />
        </Stack>
      </DialogContent>

      {isEditType && (
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => onClose()}
            disabled={checkSubmitDisabling()}
            //
          >
            {t('profile-bank-accounts.state-reason-modal.confirm-btn')}
          </Button>

          <Button variant="outlined" onClick={() => closeHandler()}>
            {t('profile-bank-accounts.state-reason-modal.cancel-btn')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
